import { Checkbox } from '@/components/ui/checkbox'
import { FormField } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import Select from 'app/frontend/base/components/Select'
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FieldWrapper from './FieldWrapper'
import { FieldProps } from './types'
import { TFunction } from 'i18next'
import { Check } from 'lucide-react'

type SettingOptionPreviewArgs = {
  option: string
  label: string
  isSelected: boolean
}

type Props = FieldProps<string> & {
  renderOptionPreview?: (args: SettingOptionPreviewArgs) => React.ReactNode
}

export const transformOptions = (
  t: TFunction,
  name: string,
  value?: string[],
) => {
  return (value || []).map((option) => ({
    label: t(`settings.${name}.options.${option}`),
    value: option,
  }))
}

const SettingField = ({
  layout = 'inline',
  name,
  renderOptionPreview,
}: Props) => {
  const { t } = useTranslation()
  const { control, watch, setValue } = useFormContext()
  const setting: wowaudit.Setting<unknown> = watch(`settings.${name}`)

  const fieldElement = (
    field: ControllerRenderProps<FieldValues, `settings.${string}.value`>,
  ) => {
    if (setting.type === 'boolean') {
      return <Checkbox checked={field.value} onCheckedChange={field.onChange} />
    }

    if (setting.multiple) {
      const options = transformOptions(t, name, setting.options || [])
      return (
        <MultiSelect<string>
          options={options}
          value={setting.value as string[]}
          onChange={(selected) =>
            setValue(`settings.${name}.value`, selected, { shouldDirty: true })
          }
        />
      )
    }

    if (setting.options) {
      const selectOptions = transformOptions(
        t,
        name,
        setting.options || [],
      ).map((option) => ({
        ...option,
        customOptionFn: renderOptionPreview
          ? (isSelected: boolean) => (
              <>
                <div className='flex w-full items-center gap-2'>
                  <span className='grow'>{option.label}</span>
                  <Check
                    size={16}
                    className={`ml-auto min-w-4 ${
                      isSelected ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                </div>
                <div className='w-full'>
                  {renderOptionPreview({
                    option: option.value,
                    label: option.label as string,
                    isSelected,
                  })}
                </div>
              </>
            )
          : undefined,
      }))

      return (
        <Select
          value={setting.value as string}
          options={selectOptions}
          className={
            layout === 'box' ? `absolute right-3 top-3 w-50 z-20 h-8` : ''
          }
          setValue={(value) =>
            setValue(`settings.${name}.value`, value, { shouldDirty: true })
          }
        />
      )
    }

    return (
      <Input
        type={setting.type}
        {...field}
        className={layout === 'box' ? `absolute right-3 top-1.5 w-24 z-20` : ''}
        value={(setting.value as string) ?? ''}
        onChange={(event) =>
          setValue(`settings.${name}.value`, event.target.value ?? '', {
            shouldDirty: true,
          })
        }
      />
    )
  }

  return (
    <div>
      <FormField
        control={control}
        name={`settings.${name}.value`}
        render={({ field }) => {
          return (
            <FieldWrapper
              layout={layout}
              label={t(`settings.${name}.label`)}
              description={t(`settings.${name}.description`)}
            >
              {fieldElement(field)}
            </FieldWrapper>
          )
        }}
      />
    </div>
  )
}

export default SettingField
